import React, { useCallback, useContext, useState } from 'react';
import {
  AppleLoginButton,
  AppleLoginIcon,
} from '../AppleLoginButton/AppleLoginButton';
import s from './SocialAuth.scss';
import {
  FacebookLoginButton,
  FacebookLoginIcon,
} from '../FacebookLoginButton/FacebookLoginButton';
import {
  GoogleLoginButton,
  GoogleLoginIcon,
} from '../GoogleLoginButton/GoogleLoginButton';
import { observer } from 'mobx-react';
import { AppContextProvider } from '../AppLoader';
import { Trans, useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { SocialLoginError } from '../SocialLoginButton';
import { TransitionToStudioModal } from '../TransitionToStudioModal/TransitionToStudioModal';
import { Box } from '@wix/design-system';
import { ThemedText, ThemedTextButton } from '../ThemedComponents';
import { keys } from '../../translationsKeys';
import GoogleIcon from '../../../images/google-icon.png';
import FacebookIcon from '../../../images/fb-icon.png';
import { LOGIN_PAGE_CONTEXT } from '../../utils/constants';

interface SocialAuthProps {
  width?: string;
  withApple?: boolean;
  alignItems?: 'center' | 'flex-end' | 'flex-start';
}

export const UnsupportedSocialWrapper: React.FC<SocialAuthProps> = observer(
  ({ children }) => {
    const {
      rootStore: {
        socialAuthStore,
        emailStepStore,
        displayStore: { isMobile },
      },
    } = useContext(AppContextProvider);
    const { t } = useTranslation();
    const [copyLinkText, setCopyLinkText] = useState(
      t('unsupported.browser.for.social.copy.link'),
    );
    const copyUrl = useCallback(() => {
      navigator.clipboard.writeText(window.location.href);
      setCopyLinkText(t('unsupported.browser.for.social.copy.link.success'));
    }, []);

    return isMobile && socialAuthStore.isUnsupportedAgentForSocialAuth ? (
      <Box
        direction="vertical"
        marginInlineStart="auto"
        marginInlineEnd="auto"
        marginTop={6}
        className={classNames(s.socialAuthContainer, {
          [s.unsupportedSocialAuth]:
            socialAuthStore.isUnsupportedAgentForSocialAuth,
        })}
      >
        <Box
          direction="horizontal"
          marginTop={1}
          marginBottom={1}
          gap={2}
          className={classNames(s.unsupportedSocialIcons)}
        >
          <img src={GoogleIcon} alt={t('icon.google')} />
          <img src={FacebookIcon} alt={t('icon.facebook')} />
        </Box>
        <Box width="255px" direction="vertical" align="center">
          <Trans
            i18nKey={
              keys[
                emailStepStore.getContext() === LOGIN_PAGE_CONTEXT
                  ? 'unsupported.browser.for.social.login.message'
                  : 'unsupported.browser.for.social.signup.message'
              ]
            }
            values={{ copyLink: copyLinkText }}
          >
            <ThemedText size="small">
              To sign up with Google or Facebook, open this link in another
              browser.
            </ThemedText>
            <ThemedTextButton
              size="small"
              as="a"
              underline="always"
              onClick={copyUrl}
            >
              Copy link
            </ThemedTextButton>
          </Trans>
        </Box>
      </Box>
    ) : (
      <>{children}</>
    );
  },
);

export const SocialButtonsContainer: React.FC<SocialAuthProps> = observer(
  ({ width = '100%', alignItems, children }) => {
    const {
      rootStore: {
        displayStore: { isVerticalLayout },
        experiments,
        emailStepStore,
      },
    } = useContext(AppContextProvider);
    const { t } = useTranslation();

    const isNewGoogleSdk = experiments?.enabled(
      'specs.ident.UseNewGoogleSdkReactLoginApp',
    );

    const socialGroupLabel =
      emailStepStore.getContext() === LOGIN_PAGE_CONTEXT
        ? t('signin_social.more_login_options')
        : t('signin_social.more_signup_options');

    return (
      <div
        style={{ width, alignItems }}
        role="group"
        aria-label={socialGroupLabel}
        className={classNames(
          s.socialAuthContainer,
          s.buttons,
          s.socialButtonsContainer,
          {
            [s.vertical]: isVerticalLayout,
            [s.newGoogleSdkStyle]: isNewGoogleSdk,
          },
        )}
      >
        {children}
        <TransitionToStudioModal />
      </div>
    );
  },
);

export const SocialAuth: React.FC<SocialAuthProps> = observer(
  ({ width, alignItems }) => {
    const {
      rootStore: { socialAuthStore },
    } = useContext(AppContextProvider);
    return (
      <UnsupportedSocialWrapper>
        <SocialButtonsContainer width={width} alignItems={alignItems}>
          {socialAuthStore.showGoogle && <GoogleLoginButton />}
          {socialAuthStore.showFacebook && <FacebookLoginButton />}
          {socialAuthStore.showApple && <AppleLoginButton />}
        </SocialButtonsContainer>
      </UnsupportedSocialWrapper>
    );
  },
);

export const SocialAuthWithoutApple: React.FC<SocialAuthProps> = observer(
  ({ width }) => {
    const {
      rootStore: { socialAuthStore },
    } = useContext(AppContextProvider);
    return (
      <UnsupportedSocialWrapper>
        <SocialButtonsContainer width={width}>
          {socialAuthStore.showGoogle && <GoogleLoginButton />}
          {socialAuthStore.showFacebook && <FacebookLoginButton />}
        </SocialButtonsContainer>
      </UnsupportedSocialWrapper>
    );
  },
);

export const SocialAuthIcons: React.FC<SocialAuthProps> = observer(
  ({ width = '100%', withApple = true, children }) => {
    const {
      rootStore: {
        displayStore: { isVerticalLayout },
        emailStepStore,
      },
    } = useContext(AppContextProvider);
    const { t } = useTranslation();
    const socialGroupLabel =
      emailStepStore.getContext() === LOGIN_PAGE_CONTEXT
        ? t('signin_social.more_login_options')
        : t('signin_social.more_signup_options');
    return (
      <UnsupportedSocialWrapper>
        <div className={s.socialAuthContainer}>
          <div
            style={{ width }}
            role="group"
            aria-label={socialGroupLabel}
            className={classNames(s.socialIconsContainer, s.icons, {
              [s.mobileIcon]: !withApple,
              [s.vertical]: isVerticalLayout,
            })}
          >
            {children}
          </div>
        </div>
      </UnsupportedSocialWrapper>
    );
  },
);

export const SocialAuthIconsWithApple: React.FC<SocialAuthProps> = ({
  width = '100%',
}) => {
  const {
    rootStore: { socialAuthStore },
  } = useContext(AppContextProvider);

  return (
    <SocialAuthIcons width={width}>
      {socialAuthStore.showGoogle && (
        <GoogleLoginIcon className={s.googleIconMobile} />
      )}
      {socialAuthStore.showFacebook && (
        <FacebookLoginIcon className={s.facebookIconMobile} />
      )}
      {socialAuthStore.showApple && (
        <AppleLoginIcon className={s.appleIconMobile} />
      )}
    </SocialAuthIcons>
  );
};

export const SocialAuthIconsWithoutApple: React.FC<SocialAuthProps> = ({
  width = '100%',
}) => {
  const {
    rootStore: { socialAuthStore },
  } = useContext(AppContextProvider);

  return (
    <SocialAuthIcons width={width} withApple={false}>
      {socialAuthStore.showFacebook && (
        <FacebookLoginIcon className={s.facebookIconMobile} />
      )}
      {socialAuthStore.showGoogle && (
        <GoogleLoginIcon className={s.googleIconMobile} />
      )}
    </SocialAuthIcons>
  );
};
